import { useState } from 'react';

interface Resource {
  title: string;
  url: string;
  source: 'Microsoft Learn' | 'Azure Pricing';
}

type ServiceKey =
  | 'blob-storage'
  | 'logic-apps'
  | 'functions'
  | 'app-service'
  | 'bandwidth'
  | 'log-analytics'
  | 'front-door'
  | 'firewall'
  | 'virtual-desktop';

const RESOURCES: Record<ServiceKey, Resource[]> = {
  'blob-storage': [
    {
      title: 'Azure Blob Storage pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/storage/blobs/',
      source: 'Azure Pricing',
    },
    {
      title: 'Understanding block blob billing',
      url: 'https://learn.microsoft.com/en-us/azure/storage/blobs/storage-blob-block-blob-premium',
      source: 'Microsoft Learn',
    },
  ],
  'logic-apps': [
    {
      title: 'Logic Apps pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/logic-apps/',
      source: 'Azure Pricing',
    },
    {
      title: 'Logic Apps billing model',
      url: 'https://learn.microsoft.com/en-us/azure/logic-apps/logic-apps-pricing',
      source: 'Microsoft Learn',
    },
  ],
  functions: [
    {
      title: 'Azure Functions pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/functions/',
      source: 'Azure Pricing',
    },
    {
      title: 'Functions consumption plan billing',
      url: 'https://learn.microsoft.com/en-us/azure/azure-functions/consumption-plan',
      source: 'Microsoft Learn',
    },
  ],
  'app-service': [
    {
      title: 'App Service pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/app-service/windows/',
      source: 'Azure Pricing',
    },
    {
      title: 'App Service plan overview',
      url: 'https://learn.microsoft.com/en-us/azure/app-service/overview-hosting-plans',
      source: 'Microsoft Learn',
    },
  ],
  bandwidth: [
    {
      title: 'Bandwidth pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/bandwidth/',
      source: 'Azure Pricing',
    },
    {
      title: 'Outbound data transfer pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/data-transfers/',
      source: 'Azure Pricing',
    },
  ],
  'log-analytics': [
    {
      title: 'Azure Monitor pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/monitor/',
      source: 'Azure Pricing',
    },
    {
      title: 'Restore logs documentation',
      url: 'https://learn.microsoft.com/en-us/azure/azure-monitor/logs/restore',
      source: 'Microsoft Learn',
    },
    {
      title: 'Log Analytics cost calculations',
      url: 'https://learn.microsoft.com/en-us/azure/azure-monitor/logs/cost-logs',
      source: 'Microsoft Learn',
    },
  ],
  'front-door': [
    {
      title: 'Azure Front Door pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/frontdoor/',
      source: 'Azure Pricing',
    },
    {
      title: 'Understand Azure Front Door billing',
      url: 'https://learn.microsoft.com/en-us/azure/frontdoor/billing',
      source: 'Microsoft Learn',
    },
    {
      title: 'Front Door classic to Standard/Premium migration',
      url: 'https://learn.microsoft.com/en-us/azure/frontdoor/tier-migration',
      source: 'Microsoft Learn',
    },
  ],
  firewall: [
    {
      title: 'Azure Firewall pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/azure-firewall/',
      source: 'Azure Pricing',
    },
    {
      title: 'Azure Firewall documentation',
      url: 'https://learn.microsoft.com/en-us/azure/firewall/overview',
      source: 'Microsoft Learn',
    },
    {
      title: 'Azure Firewall logs and metrics',
      url: 'https://learn.microsoft.com/en-us/azure/firewall/logs-and-metrics',
      source: 'Microsoft Learn',
    },
  ],
  'virtual-desktop': [
    {
      title: 'Azure Virtual Desktop pricing',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/virtual-desktop/',
      source: 'Azure Pricing',
    },
    {
      title: 'Azure Files pricing for FSLogix profiles',
      url: 'https://azure.microsoft.com/en-gb/pricing/details/storage/files/',
      source: 'Azure Pricing',
    },
    {
      title: 'FSLogix profile containers guidance',
      url: 'https://learn.microsoft.com/en-us/fslogix/concepts-container-profile',
      source: 'Microsoft Learn',
    },
  ],
};

const SOURCE_STYLE: Record<Resource['source'], string> = {
  'Microsoft Learn': 'text-[#107c10] border-[#107c10]/40',
  'Azure Pricing': 'text-[#0078d4] border-[#0078d4]/40',
};

interface ResourcesSectionProps {
  service: ServiceKey;
}

export function ResourcesSection({ service }: ResourcesSectionProps) {
  const [open, setOpen] = useState(false);
  const resources = RESOURCES[service];

  return (
    <div className="rounded" style={{ border: '1px solid var(--outline-variant)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
        style={{ backgroundColor: 'transparent' }}
      >
        <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined text-[16px]">link</span>
          Sources &amp; Resources
        </span>
        <span
          className="material-symbols-outlined text-[18px] transition-transform"
          style={{ color: 'var(--text-tertiary)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className="flex flex-col" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          {resources.map((r) => (
            <a
              key={r.url}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-4 py-3 transition-colors"
              style={{ borderBottom: '1px solid var(--surface-container)' }}
            >
              <span
                className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${SOURCE_STYLE[r.source]}`}
              >
                {r.source}
              </span>
              <span className="flex-1 text-[13px] transition-colors leading-snug" style={{ color: 'var(--text-secondary)' }}>
                {r.title}
              </span>
              <span className="material-symbols-outlined text-[16px] shrink-0 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                open_in_new
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
